import React, { useEffect } from 'react'

import get from 'lodash.get'
import { useParams } from 'react-router-dom'

import 'pages/MomentDetail/stylesheet.scss'
import SoundBiteDetailContent, { KaraokeFallback } from 'components/SoundBite/SoundBiteDetailContent'
import Spinner from 'components/Template/Spinner'
import useIsDesktop from 'hooks/useIsDesktop'
import EpisodeDetailPageButton from 'pages/Listen/EpisodeDetail/EpisodeDetailPageButton'
import { useMomentDetailQuery } from 'pages/MomentDetail/queries'
import { page } from 'utils/monitoring'
import { TRACKING_PAGE_NAMES } from 'utils/ts-misc'

const PAGE_NAME = TRACKING_PAGE_NAMES.momentDetail

/**
 * Standalone page representing a Moment that has been shared.
 */
export default function MomentDetail() {
    const { momentHashid } = useParams() as Record<string, string>
    const { data: moment, isLoading } = useMomentDetailQuery(momentHashid)
    const isDesktop = useIsDesktop()

    useEffect(() => {
        page('MomentDetail', { moment_hashid: momentHashid })
    }, [])

    if (isLoading) return <Spinner />

    const audioProperties: MomentAudioProperties = {
        content_type: 'moment',
        hashid: momentHashid,
        page: PAGE_NAME,
        source: get(moment, 'audio_file_presigned_url', null),
        title: `Highlight ${momentHashid}`
    }

    const sharingProperties: SharingProperties = {
        content_type: 'moment',
        entrypoint: 'moment_detail_page',
        has_title: Boolean(moment.title),
        hashid: moment.hashid,
        title: moment.title_or_meta_title,
        url: moment.url
    }

    const footer = (
        <div className='moment-detail-footer-container justify-end'>
            <EpisodeDetailPageButton
                contentType={audioProperties.content_type}
                episodeSlug={moment.episode_slug}
                hashid={audioProperties.hashid}
                label='Keep playing'
                pageName={PAGE_NAME}
            />
        </div>
    )

    return (
        <SoundBiteDetailContent
            audioProperties={audioProperties}
            episodeCoverImageUrl={moment.episode.image_url}
            fallback={<KaraokeFallback subtitle='A highlight from' title={moment.episode.title} />}
            footer={footer}
            header={moment.episode.title}
            images={moment.images}
            isDesktop={isDesktop}
            sharingProperties={sharingProperties}
            speakerMapping={moment.speaker_mapping}
            subheader={moment.title}
            timeOffset={moment.timestamp_start}
            utterances={moment.utterances}
        />
    )
}
